import { NavigationLoadingLink } from '@features/components/Navigation/NavigationLoadingLink';
import AppsSvg from '@icons/apps.svg?react';
import DashboardSvg from '@icons/dashboard.svg?react';
import LogoSvg from '@icons/logo.svg?react';
import TranslateSvg from '@icons/translate.svg?react';
import UserAvatarSvg from '@shared/icons/user-avatar.svg?react';
import { Avatar } from '@shared/ui/Avatar';
import ThemeToggler from '@shared/ui/ThemeToggler/ThemeToggler';
import localStore from '@shared/utils/localStore';
import { useAppDispatch, useAppSelector } from '@store/hooks.ts';
import { selectTheme } from '@store/reducers/appSlice';
import { logout } from '@store/reducers/authSlice.ts';
import type { UserDto } from '@store/services/authApi.ts';
import classNames from 'classnames';
import { t } from 'i18next';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { NavigationName } from './model/NavigationEnum';

interface TopNavigationProps {
  ref?: React.Ref<HTMLDivElement>;
  fetchingUser?: boolean;
  userData?: UserDto;
}

const TopNavigation: FC<TopNavigationProps> = ({ ref, userData, fetchingUser }) => {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useAppSelector(selectTheme);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStore.setLanguage(lng);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const navigationPaths: Record<NavigationName, string[]> = {
    [NavigationName.APP]: [
      '/',
      '/home',
      '/question',
      '/more',
      '/connection',
      '/connection/edit',
      '/discussion',
    ],
    [NavigationName.DASHBOARD]: [
      '/admin/dashboard',
      '/admin/user',
      '/admin/content',
      '/admin/permission',
      '/admin/question',
      '/admin/permissionModule',
      '/admin/permissionRole',
    ],
  };

  const navigationLinks: Record<NavigationName, string> = {
    [NavigationName.APP]: '/',
    [NavigationName.DASHBOARD]: '/admin/dashboard',
  };

  const isActive = (nav: NavigationName) => {
    return navigationPaths[nav].includes(window.location.pathname);
  };

  const renderMenuItem = (menuName: NavigationName, icon: React.ReactNode) => {
    return (
      <NavigationLoadingLink
        to={navigationLinks[menuName]}
        className={classNames('flex gap-2 text-xs rounded-lg', {
          'bg-base-content shadow': isActive(menuName),
          'text-base-200': isActive(menuName),
        })}
        loadingIcon={<span className="loading loading-spinner loading-sm" />}
      >
        {icon}
        <span className="uppercase">{t(`navigation.${menuName}`)}</span>
      </NavigationLoadingLink>
    );
  };

  return (
    <nav
      ref={ref}
      className="w-full fixed right-0 flex bg-base-100 z-20 py-2.5 px-4 shadow-xs backdrop-blur-xl items-center"
    >
      <div className="flex w-full max-w-480 mx-auto">
        <div className="inline-flex items-center">
          <Link to="/" className="btn btn-ghost px-2 flex gap-3">
            <LogoSvg className="inline-flex h-8 w-8 fill-primary" />
            <div className="text-2xl font-extrabold">{t('settings.app.name')}</div>
          </Link>
        </div>

        <div className="ml-auto flex items-center gap-4 relative">
          <div className="flex items-center">
            <ul className="menu menu-horizontal pl-8 gap-3 flex items-center">
              <li>
                {renderMenuItem(
                  NavigationName.APP,
                  <AppsSvg className="h-6 w-6 stroke-current fill-none" />,
                )}
              </li>
              {userData?.isSuperAdmin && (
                <li>
                  {renderMenuItem(
                    NavigationName.DASHBOARD,
                    <DashboardSvg className="h-6 w-6 stroke-current fill-none" />,
                  )}
                </li>
              )}
            </ul>
          </div>

          <div className="flex gap-2">
            <ThemeToggler />
            <div className="dropdown dropdown-end">
              <button
                tabIndex={0}
                className="btn btn-ghost btn-sm m-1 opacity-60 hover:opacity-100"
              >
                <TranslateSvg className="stroke-base-content h-5 w-5" />
              </button>
              <ul className="dropdown-content z-1 menu p-2 shadow-sm bg-base-100 rounded-md w-52">
                <li onClick={() => changeLanguage('cnt')}>
                  <button>繁体中文</button>
                </li>
                <li onClick={() => changeLanguage('cn')}>
                  <button>简体中文</button>
                </li>
                <li onClick={() => changeLanguage('en')}>
                  <button>English</button>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex flex-col justify-center items-end gap-0.5">
              {userData ? (
                <div className="text-sm font-bold">{userData.name}</div>
              ) : (
                <div className="text-sm font-bold">{t('users.tips.visitor')}</div>
              )}

              {fetchingUser && <span className="skeleton w-20 h-5 rounded-sm" />}
            </div>

            <div className="dropdown dropdown-hover dropdown-end hover:scale-110 cursor-pointer">
              {userData ? (
                <Avatar
                  size="md"
                  shape="rounded"
                  alt="User-Avatar"
                  uuid={userData.id}
                  mode={theme}
                />
              ) : (
                <div className="avatar m-1">
                  <div className="w-8 rounded-full">
                    <div className="w-8 h-8 rounded-full bg-base-300 flex items-center justify-center">
                      <UserAvatarSvg className="w-4 h-4 stroke-current fill-none" />
                    </div>
                  </div>
                </div>
              )}

              {fetchingUser && <div className="skeleton rounded-full h-8 w-8" />}

              <ul className="dropdown-content z-9999 menu p-2 shadow-sm bg-base-100 rounded-md w-52">
                {userData && (
                  <li onClick={handleLogout}>
                    <a>退出登录</a>
                  </li>
                )}
                {!userData && !fetchingUser && (
                  <li onClick={handleLogin}>
                    <a>登录</a>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopNavigation;
